import { CheckSquare, Check, X } from 'lucide-react';
import { useStore } from '@/store/StoreContext';
import { Card, SectionTitle, EmptyState } from '@/components/ui';
import { trainingDates } from '@/lib/constants';

export function AttendanceView() {
  const { activeClass, activeClassAgents, updateAgentAttendance } = useStore();
  const userRole = localStorage.getItem('uisce-user-role');
  const isReadOnly = userRole === 'viewer' || userRole === 'guest' || userRole === 'manager';

  if (!activeClass || activeClassAgents.length === 0) {
    return (
      <div className="space-y-6 animate-fade-in">
        <SectionTitle
          title="Attendance & Hypercare Tracker"
          subtitle="Monitor daily attendance and 3 days of hypercare support."
          icon={<CheckSquare className="w-6 h-6" />}
        />
        <Card className="p-12 text-center text-slate-500">
          <EmptyState message="No active class or trainees found. Please add trainees to the roster first." />
        </Card>
      </div>
    );
  }

  const dates = trainingDates(activeClass.startDate);

  const handleCycle = (agentId: string, dateStr: string, currentRecord: boolean | undefined) => {
    // STRICT READ-ONLY GUARD AT TOP OF HANDLER
    if (isReadOnly) {
      alert('Guest / Manager accounts have read-only access.');
      return;
    }

    let nextStatus: boolean | undefined;
    if (currentRecord === undefined) {
      nextStatus = true; // Blank -> Present
    } else if (currentRecord === true) {
      nextStatus = false; // Present -> Absent
    } else {
      nextStatus = undefined; // Absent -> Blank
    }

    updateAgentAttendance(agentId, dateStr, nextStatus);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionTitle
        title="Attendance & Hypercare Tracker"
        subtitle={`15 Training Days + 3 Hypercare Days for ${activeClass.className} (Starting ${activeClass.startDate})`}
        icon={<CheckSquare className="w-6 h-6" />}
      />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/75 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-6 py-3.5 sticky left-0 bg-slate-50 z-10">Trainee Name</th>
                {dates.map((d, index) => {
                  const isHypercare = index >= 15;
                  return (
                    <th key={d.date} className={`px-2 py-3.5 text-center min-w-[75px] ${isHypercare ? 'bg-blue-50/50' : ''}`}>
                      <div className={`text-[11px] font-bold ${isHypercare ? 'text-blue-700' : 'text-slate-700'}`}>
                        {isHypercare ? `Hypercare ${index - 14}` : `Day ${index + 1}`}
                      </div>
                      <div className="text-[10px] text-slate-400 font-normal">{d.date.slice(5)}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {activeClassAgents.map((agent) => (
                <tr key={agent.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="px-6 py-4 font-semibold text-slate-900 sticky left-0 bg-white z-10 whitespace-nowrap">
                    <div>{agent.name}</div>
                    <div className="text-xs font-mono font-normal text-slate-400">{agent.pNumber}</div>
                  </td>
                  {dates.map((d, index) => {
                    const record = agent.attendance.find((a) => a.date === d.date);
                    const status = record ? record.present : undefined;
                    const isHypercare = index >= 15;

                    return (
                      <td key={d.date} className={`px-2 py-4 text-center ${isHypercare ? 'bg-blue-50/20' : ''}`}>
                        <button
                          type="button"
                          onClick={() => handleCycle(agent.id, d.date, status)}
                          disabled={isReadOnly}
                          className={`w-8 h-8 rounded-lg mx-auto flex items-center justify-center transition-all ${
                            isReadOnly ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'
                          } ${
                            status === true
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-300 shadow-xs'
                              : status === false
                              ? 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-300 shadow-xs'
                              : 'bg-slate-100 text-slate-400 hover:bg-slate-200 border border-slate-200'
                          }`}
                          title={
                            isReadOnly
                              ? 'Read-only access'
                              : status === true
                              ? 'Present (Click to mark Absent)'
                              : status === false
                              ? 'Absent (Click to clear)'
                              : 'Unmarked (Click to mark Present)'
                          }
                        >
                          {status === true && <Check className="w-4 h-4 stroke-[2.5]" />}
                          {status === false && <X className="w-4 h-4 stroke-[2.5]" />}
                          {status === undefined && <span className="text-[10px] text-slate-400">-</span>}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
